"use client";
import { useEffect, useRef } from "react";
import { useHeaderHeight } from "@/app/providers/navigationProvider";

/**
 * A custom hook that calculates and applies a dynamic sticky offset for elements
 * positioned under a shrinking header.
 * * It uses a `ResizeObserver` to track the header's real-time height, calculates
 * how much it has shrunk from its maximum height, and applies the exact pixel
 * difference to a CSS variable (`--dynamic-sticky-top`) on the target element.
 *
 * @param {number} [baseOffset=20] - The initial gap (in pixels) you want between the
 * header and the sticky element when the header is
 * fully expanded. Defaults to 20.
 * * @returns {React.RefObject<HTMLDivElement>} A ref to be attached to the sticky container element.
 *
 * @example
 * // 1. Call the hook
 * const stickyRef = useStickyOffset(24);
 * * // 2. Attach the ref and use the CSS variable in your styling (e.g., Tailwind)
 * <div
 * ref={stickyRef}
 * className="sticky top-[var(--dynamic-sticky-top,24px)]"
 * >
 * Sticky Content
 * </div>
 */
export const useStickyOffset = (baseOffset: number = 20) => {
  const { headerContentRef, headerHeight: maxHeaderHeight } = useHeaderHeight();
  const stickyContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let observer: ResizeObserver;
    let timeoutId: NodeJS.Timeout;

    const initObserver = () => {
      const headerEl = headerContentRef?.current;
      const stickyEl = stickyContainerRef.current;

      if (!headerEl || !stickyEl || maxHeaderHeight === 0) {
        timeoutId = setTimeout(initObserver, 50);
        return;
      }

      observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        const currentHeight =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;

        const shrinkDifference = maxHeaderHeight - currentHeight;
        const dynamicTop = baseOffset - shrinkDifference;

        stickyEl.style.setProperty("--dynamic-sticky-top", `${dynamicTop}px`);
      });

      observer.observe(headerEl);

      const currentHeight = headerEl.getBoundingClientRect().height;
      const shrinkDifference = maxHeaderHeight - currentHeight;
      stickyEl.style.setProperty(
        "--dynamic-sticky-top",
        `${baseOffset - shrinkDifference}px`
      );
    };

    initObserver();

    return () => {
      clearTimeout(timeoutId);
      if (observer) observer.disconnect();
    };
  }, [headerContentRef, maxHeaderHeight, baseOffset]);

  return stickyContainerRef;
};
